class ImageHeroSlider {
  constructor(container) {
    this.container = container;
    this.slides = container.querySelectorAll('.img-hero__slide');
    this.dots = container.querySelectorAll('.img-hero__nav-dot');
    this.prevBtn = container.querySelector('.img-hero__arrow--prev');
    this.nextBtn = container.querySelector('.img-hero__arrow--next');
    this.toggleBtn = container.querySelector('.img-hero__autoplay');
    this.currentSlide = 0;
    this.autoplayInterval = null;

    // Get settings from section data or use defaults
    this.autoplay = this.container.dataset.autoplay !== 'false';
    this.duration = parseInt(this.container.dataset.duration || '5') * 1000;

    // MOT-01: once the visitor stops the slideshow it stays stopped. Hover and focus
    // suspend it temporarily; this flag is the deliberate choice and outranks both.
    this.userPaused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.init();
  }

  init() {
    if (this.slides.length <= 1) return;

    // Add event listeners
    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this.toggleAutoplay());
    }

    // Touch/swipe support
    this.addTouchSupport();

    if (this.reducedMotion.matches) {
      this.userPaused = true;
    }
    this.autoplay = this.autoplay && !this.userPaused;

    // Start autoplay if enabled
    if (this.autoplay) {
      this.startAutoplay();
    }
    this.syncToggle();

    // Pause autoplay on hover
    this.container.addEventListener('mouseenter', () => this.stopAutoplay());
    this.container.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startAutoplay();
    });

    // …and on keyboard focus, so a slide cannot change out from under the caret.
    this.container.addEventListener('focusin', () => this.stopAutoplay());
    this.container.addEventListener('focusout', (event) => {
      if (this.autoplay && !this.container.contains(event.relatedTarget)) this.startAutoplay();
    });
  }

  goToSlide(index) {
    if (index === this.currentSlide) return;

    // Remove active classes
    this.slides[this.currentSlide].classList.remove('img-hero__slide--active');
    this.slides[this.currentSlide].inert = true;
    if (this.dots.length > 0) {
      this.dots[this.currentSlide].classList.remove('img-hero__nav-dot--active');
      this.dots[this.currentSlide].removeAttribute('aria-current');
    }

    // Update current slide
    this.currentSlide = index;

    // Add active classes
    this.slides[this.currentSlide].classList.add('img-hero__slide--active');
    this.slides[this.currentSlide].inert = false;
    if (this.dots.length > 0) {
      this.dots[this.currentSlide].classList.add('img-hero__nav-dot--active');
      this.dots[this.currentSlide].setAttribute('aria-current', 'true');
    }

    // Reset autoplay timer
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  toggleAutoplay() {
    this.userPaused = !this.userPaused;
    this.autoplay = !this.userPaused;
    if (this.autoplay) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
    this.syncToggle();
  }

  syncToggle() {
    if (!this.toggleBtn) return;
    this.toggleBtn.classList.toggle('media-toggle--paused', this.userPaused);
    this.toggleBtn.setAttribute(
      'aria-label',
      this.userPaused ? this.toggleBtn.dataset.labelPlay : this.toggleBtn.dataset.labelPause
    );
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.userPaused) return;
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.duration);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  addTouchSupport() {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    });

    this.container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;

      const deltaX = endX - startX;
      const deltaY = endY - startY;

      // Only handle horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          this.prevSlide();
        } else {
          this.nextSlide();
        }
      }
    });
  }
}

// Initialize sliders when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.img-hero');
  sliders.forEach(slider => {
    if (slider.querySelectorAll('.img-hero__slide').length > 1) {
      new ImageHeroSlider(slider);
    }
  });
});

// Re-initialize on Shopify section reload (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const slider = event.target.querySelector('.img-hero');
  if (slider && slider.querySelectorAll('.img-hero__slide').length > 1) {
    new ImageHeroSlider(slider);
  }
});
